/* eslint-disable jsx-a11y/media-has-caption */
import React, {
  useRef,
  useReducer,
  useEffect,
  useState,
  useLayoutEffect,
  useMemo
} from "react";
import StackGrid from "react-stack-grid";
import QRCode from "react-qr-code";
import { useHistory, useLocation } from "react-router-dom";

import client, { isSignedIn } from "@culturehq/client";
import {
  Badge,
  Button,
  Circles,
  Icon,
  Loader,
  Nav,
  PlainButton,
  classnames
} from "@culturehq/components";

import { useDoor, useIsMobile, useIsTablet } from "app/hooks";
import { isAdmin } from "app/permissions";
import { Trans, getTrend, getSubdomain, getUniqueUsers, t, checkArray } from "app/utils";

import NotFound from "components/NotFound";
import NewStoryQuestionModal from "components/admin/settings/story-questions/NewStoryQuestionModal";
import ErrorBoundary from "components/common/ErrorBoundary";
import ErrorMessage from "components/common/ErrorMessage";
import StoryThumbnail from "components/common/StoryThumbnail";
import FeedItemPlaceholder from "components/feed/FeedItemPlaceholder";
import LightboxStories from "components/common/gallery/LightboxStories";
import Subtitles from "components/common/Subtitles";
import FeedItemTitle from "components/feed/feed-item/preview/FeedItemTitle";
import QuickLinks from "components/feed/feed-item/preview/story-created/QuickLinks";
import FeedItemFooter from "components/feed/feed-item/Footer";
import ShareQuickLinks from "components/stories/ShareQuickLinks";
import { ProfileLink } from "app/links";
import { useProfile } from "app/context";
import { usePublic } from "app/context/PublicContext";
import { getIcon } from "constants/tagsTypes";
import { FeedItemComments } from "components/Comments";
import ChqModalDialog from "components/common/ChqModalDialog";
import PublicNotificationPopup from "components/notifications/PublicNotificationPopup";
import AnswerPrompts from "components/stories/AnswerPrompts";
import DiscoverStories from "components/stories/DiscoverStories";
import PromptShareOptions from "components/stories/PromptShareOptions";
import StoryQuestionTitle from "components/stories/StoryQuestionTitle";
import StoryTrendSummary from "components/stories/StoryTrendSummary";
import StartTheTrend from "components/stories/StartTheTrend";
import PostStoryModal from "components/ai/stories/principal-modal/PostStoryModal";
import PostStory from "components/stories/post-stories-v2/PostStory";
import VideoStudioContext from "components/stories/video-studio/contexts/VideoStudioContext";
import magicWandIcon from "images/magic-wand_gradient.svg";
import CrossTenantWarning from "components/common/CrossTenantWarning/CrossTenantWarning";
import checkCrossTenant from "app/utils/checkCrossTenant";
import StoryTrendNotification from "./StoryTrendNotification";
import StoryPromptaiAssistant from "./StoryPromptaiAssistant";

const initialState = {
  appending: null,
  appendingImages: false,
  error: null,
  fetching: true,
  pagination: {},
  stories: [],
  userActivity: { id: null, entity: {} },
  usersCount: 0,
  currentUserAnswered: false,
  showComments: true,
  storyQuestion: undefined
};

const reducer = (state, action) => {
  switch (action.type) {
    case "appendStart":
      return {
        ...state,
        appending: state.pagination.currentPage + 1,
        appendingImages: true
      };
    case "appendSuccess":
      return {
        ...state,
        appending: null,
        pagination: action.response.pagination,
        stories: [...state.stories, ...action.response.stories]
      };
    case "appendError":
      return { ...state, appending: null, error: action.error };
    case "childStoryCreated":
      return {
        ...state,
        storyQuestion: {
          ...state.storyQuestion,
          childStoryQuestions: state.storyQuestion.childStoryQuestions.map(
            childStory => (childStory.id === action.story.question.id
              ? {
                ...childStory,
                peopleWhoAnswered: [
                  ...childStory.peopleWhoAnswered,
                  action.story.creator
                ].reduce((acc, record) => {
                  if (acc.findIndex(r => r.id === record.id) !== -1) return acc;
                  acc.push(record);
                  return acc;
                }, [])
              }
              : childStory)
          )
        }
      };
    case "fetchStart":
      return { ...state, error: null, fetching: true };
    case "fetchSuccess":
      return {
        ...state,
        error: null,
        fetching: false,
        organization: action.response.organization,
        pagination: action.response.pagination,
        stories: action.response.stories,
        storyQuestion: action.response.storyQuestion,
        userActivity: action.response.userActivity || initialState.userActivity,
        usersCount: getUniqueUsers(action.response.userActivity).length,
        currentUserAnswered: getUniqueUsers(action.response.userActivity).find(
          userId => userId === action.profile.id
        )
      };
    case "fetchError":
      return { ...state, error: action.error, fetching: false };
    case "storyCreated":
      return {
        ...state,
        stories: [action.story, ...action.stories]
      };
    case "storyQuestionUpdated":
      return {
        ...state,
        storyQuestion: action.storyQuestion
      };
    case "storyUpdate":
      return {
        ...state,
        stories: state.stories.map(story => (
          story.id === action.storyId ? { ...story, ...action.updates } : story)
        )
      };
    case "storyRemove":
      return {
        ...state,
        stories: state.stories.filter(story => story.id !== action.storyId)
      };
    case "storyCommentsUpdated":
      return {
        ...state,
        stories: state.stories.map(story => {
          if (story.id === action.storyId) {
            return { ...story, commentCount: action.numberOfComments };
          }
          return story;
        })
      };
    case "toogleShowComments":
      return {
        ...state,
        showComments: !state.showComments
      };
    case "storyCheerToogle":
      return {
        ...state,
        stories: state.stories.map(story => (story.id !== action.storyId
          ? story
          : {
            ...story,
            cheerForUser: action.cheerForUser,
            cheersCount: action.cheersCount
          })
        )
      };
    case "updateUserActivity":
      return {
        ...state,
        usersCount: getUniqueUsers(action.userActivity).length,
        userActivity: action.userActivity
      };
    default:
      return state;
  }
};

const processPublicPeopleWhoAnswered = response => {
  const { storyQuestion } = response;
  return {
    ...response,
    storyQuestion: {
      ...storyQuestion,
      childStoryQuestions: storyQuestion.visibility === "public_access"
        ? checkArray(
          storyQuestion.publicChildStoryQuestions || storyQuestion.childStoryQuestions
        ).map(pcsq => ({
          ...pcsq,
          peopleWhoAnswered: pcsq.publicPeopleWhoAnswered || pcsq.peopleWhoAnswered
        }))
        : storyQuestion.childStoryQuestions
    }
  };
};

const useFetchStories = (
  dispatch,
  storyQuestionId,
  profile
) => useEffect(() => {
  let cancelled = false;
  dispatch({ type: "fetchStart" });

  client
    .listStoryTrend({ storyQuestionId })
    .then(response => {
      if (cancelled) return;

      dispatch({
        type: "fetchSuccess",
        response: processPublicPeopleWhoAnswered(response),
        profile
      });
    })
    .catch(error => {
      if (!cancelled) {
        dispatch({ type: "fetchError", error });
      }
      if (error.status === 401) window.location.replace(`${window.location.origin}/login`);
    });

  return () => {
    cancelled = true;
  };
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [storyQuestionId]);

const useAppendStories = (dispatch, appending, storyQuestionId) => useEffect(() => {
  if (appending) {
    let cancelled = false;

    client
      .listStoryTrend({ page: appending, storyQuestionId })
      .then(response => {
        if (cancelled) return;

        dispatch({
          type: "appendSuccess",
          response: processPublicPeopleWhoAnswered(response)
        });
      })
      .catch(error => {
        if (!cancelled) {
          dispatch({ type: "appendError", error });
        }
      });

    return () => {
      cancelled = true;
    };
  }

  return undefined;
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [appending, storyQuestionId]);

const useScrollListen = (dispatch, feedRef, pagination, stories) => useEffect(
  () => {
    if (pagination.currentPage !== pagination.totalPages) {
      const onScroll = () => {
        const feedBottom = feedRef.current.offsetTop + feedRef.current.offsetHeight;
        const windowBottom = window.pageYOffset + window.innerHeight;

        if (feedBottom - windowBottom < 100 && stories.length !== 0) {
          dispatch({ type: "appendStart" });
        }
      };

      document.addEventListener("scroll", onScroll);
      return () => document.removeEventListener("scroll", onScroll);
    }

    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination]
);

const getColumnWidth = storiesNumber => {
  if (window.innerWidth >= 768 && storiesNumber !== 1) {
    return "50%";
  }

  return "100%";
};

const UserActivityView = ({
  isStoryQuestionPausedStatus,
  state,
  handleThumbnailClick,
  handleCommentClick,
  handleCommentsChange,
  isParentPrompt,
  onNotification,
  currentOrganization
}) => {
  const [columnWidth, setColumnWidth] = useState(
    getColumnWidth(state.stories.length)
  );
  const isSigned = isSignedIn();
  const isAIPrompt = state.storyQuestion?.storyQuestionType === "ai_prompt";

  useEffect(() => {
    setColumnWidth(getColumnWidth(state.stories.length));
  }, [state.stories.length]);

  useLayoutEffect(() => {
    const updateSize = () => {
      setColumnWidth(getColumnWidth(state.stories.length));
    };
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, [state.stories]);

  return (
    <>
      <div className="Stories__container--images">
        {state.stories.length === 0 ? (
          <div className="EmptyResults EmptyResults-feed">
            <h2>
              <Trans>No stories have been added here yet</Trans>
            </h2>
            <h3>
              <Trans>
                The first person to answer will get the trendsetter badge!
              </Trans>
            </h3>
            {!isStoryQuestionPausedStatus && (
              <>
                {isAIPrompt && !isStoryQuestionPausedStatus ? (
                  <PostStoryModal
                    preFilledQuestion={state.storyQuestion}
                    organization={currentOrganization}
                    buttonText={<Trans>Start the trend!</Trans>}
                  />
                ) : (
                  <PostStory
                    organization={currentOrganization}
                    preFilledQuestion={
                      isParentPrompt
                        ? state.storyQuestion.childStoryQuestions[0]
                        : state.storyQuestion
                    }
                    openModal
                    prompts={state.storyQuestion.childStoryQuestions}
                    currentIndex={
                      isParentPrompt ? 1 : undefined
                    }
                    onNotification={onNotification}
                  >
                    {onTrigger => (
                      <Button onClick={onTrigger} inverted>
                        <Trans>Start the trend!</Trans>
                      </Button>
                    )}
                  </PostStory>
                )}
              </>
            )}
            <br />
          </div>
        ) : (
          <>
            <StackGrid
              columnWidth={columnWidth}
              className="Story-Stack"
              monitorImagesLoaded
            >
              {state.stories.map((story, index) => (
                <ErrorBoundary key={`${story.id}-${index}`}>
                  <StoryThumbnail
                    index={index}
                    key={`${story.id}-${index}`}
                    story={story}
                    onClick={handleThumbnailClick}
                    showQuestion
                    isTrendsetter={story.isTrendsetter}
                    ariaLabelledby
                  />
                </ErrorBoundary>
              ))}
            </StackGrid>
            {state.appending && <Loader loading />}
            {state?.storyQuestion?.storyQuestionType === "old_prompt" && (
              <>
                <div className="chq-ffd-tags">
                  {state.userActivity?.entity.question?.storyQuestionTags?.map(
                    tag => tag.entity && (
                      <Badge key={`${tag.id}`}>
                        <Icon
                          icon={getIcon(tag.entityType)}
                          className="category-icon"
                        />
                        {tag.entity.name}
                      </Badge>
                    )
                  )}
                  {state.userActivity?.entity.question?.storyQuestionTags
                    ?.length === 0 && (
                      <Badge className="Everyone--Tag">
                        <Icon icon="global" className="category-icon" />
                        Everyone
                      </Badge>
                    )}
                </div>
                {isSigned && (
                  <div className="row">
                    <FeedItemFooter
                      item={state.userActivity}
                      onCommentsClick={handleCommentClick}
                    />
                  </div>
                )}
                {state.showComments && isSigned && (
                  <div className="row">
                    <div className="FeedItem__comments">
                      <FeedItemComments
                        item={state.userActivity}
                        onChange={handleCommentsChange}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

const InstructionalVideo = ({ media, open }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const handleFullScreen = () => {
      if (videoRef.current) {
        if (videoRef.current.requestFullscreen) {
          videoRef.current.requestFullscreen();
        } else if (videoRef.current.mozRequestFullScreen) {
          videoRef.current.mozRequestFullScreen();
        } else if (videoRef.current.webkitRequestFullscreen) {
          videoRef.current.webkitRequestFullscreen();
        } else if (videoRef.current.msRequestFullscreen) {
          videoRef.current.msRequestFullscreen();
        }
      }
    };

    if (open) handleFullScreen();
  }, [open]);

  return (
    <div
      className="InstructionalVideo"
      style={{ backgroundImage: `url("${media.thumbnail}")` }}
    >
      <h3 className="InstructionalVideo--Title">
        <Trans>INTRODUCTION VIDEO</Trans>
      </h3>
      <video
        ref={videoRef}
        autoPlay={open}
        className="InstructionalVideo--Video"
        controls
        fu
        poster={`${media.thumbnail}?not-from-cache-please`}
        src={media.url}
        disablePictureInPicture
        crossOrigin="anonymous"
        controlsList="nodownload"
      >
        <Subtitles media={media} />
      </video>
    </div>
  );
};

const PublicToolbar = () => (
  <Nav
    className={classnames(
      { "chq-nav-mopen": true },
      "culturehq-nav",
      "chq-nav-full-width"
    )}
  >
    <Circles />
  </Nav>
);

const WatchIntroVideo = ({ instructionalVideoMedia, style }) => {
  const { open, onOpen, onClose } = useDoor(false);

  return (
    <>
      <Badge className="WatchIntroVideo" onClick={onOpen} style={style}>
        <Icon icon="play-no-border" className="category-icon" />
        WATCH THIS INTRO VIDEO
      </Badge>
      {open && (
        <ChqModalDialog
          onClose={onClose}
          className="UsersModal"
          entrance="zoomIn"
        >
          <InstructionalVideo open={open} media={instructionalVideoMedia} />
        </ChqModalDialog>
      )}
    </>
  );
};

const StoryItems = ({ feedRef, storyQuestionId, storyId }) => {
  const location = useLocation();
  const history = useHistory();
  const { profile } = useProfile();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [activeStory, setActiveStory] = useState(undefined);
  const [notification, setNotification] = useState();
  const isLoggedIn = isSignedIn();
  const { setQuestionId: setPublicQuestionId } = usePublic();
  const instructionalVideoMedia = state.storyQuestion?.media;
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isStoryQuestionPausedStatus = state.storyQuestion?.status === "paused";
  const isAIPrompt = state.storyQuestion?.storyQuestionType === "ai_prompt";
  const isParentPrompt = state.storyQuestion?.storyQuestionType === "parent_prompt";
  const isStandalonePrompt = state.storyQuestion?.storyQuestionType === "old_prompt";
  const currentOrganization = state.organization || profile.organization;
  const isCrossTenant = checkCrossTenant(state.organization, profile);
  const showCreateMontageButton = isLoggedIn && !!state.stories.length && !isCrossTenant
                                  && !isStoryQuestionPausedStatus;

  useFetchStories(dispatch, storyQuestionId, profile);
  useAppendStories(dispatch, state.appending, storyQuestionId);
  useScrollListen(dispatch, feedRef, state.pagination, state.stories);

  useEffect(() => {
    if (!storyQuestionId) return;

    setPublicQuestionId(storyQuestionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyQuestionId]);

  useEffect(() => {
    let isMounted = true;

    const getStory = async id => {
      const { story } = await client.getStory({ storyId: id });
      if (!isMounted) return;

      setActiveStory(story);
      setModalIsOpen(true);
    };

    if (storyId) {
      getStory(storyId);
    }

    return () => {
      isMounted = false;
    };
  }, [storyId]);

  const path = useMemo(() => {
    if (state.storyQuestion) {
      if (isParentPrompt || isAIPrompt || isStandalonePrompt) {
        return `stories/trend/${state.storyQuestion.id}`;
      }

      return `stories/trend/${state.storyQuestion.parentStoryQuestion.id}/prompt/${state.storyQuestion.id}`;
    }
    return "";
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.storyQuestion]);

  const onStoryUpdate = (updatedStory, userActivity) => {
    const parentQuestionId = updatedStory.question?.parentStoryQuestion?.id;
    const hasSameQuestionId = updatedStory.question?.id === Number(storyQuestionId);
    const hasSameParentQuestionId = parentQuestionId === Number(storyQuestionId);

    if (!hasSameQuestionId && !hasSameParentQuestionId) {
      onStoryRemove(updatedStory.id);
      return;
    }

    dispatch({
      type: "storyUpdate",
      storyId: updatedStory.id,
      updates: updatedStory
    });
    if (userActivity) dispatch({ type: "updateUserActivity", userActivity });
    setActiveStory(updatedStory);
  };

  const onStoryRemove = id => {
    setModalIsOpen(false);
    dispatch({ type: "storyRemove", storyId: id });
  };

  const handleThumbnailClick = goToIndex => {
    let target = goToIndex;

    if (goToIndex === -1) {
      target = state.stories.length - 1;
    }

    if (goToIndex > state.stories.length - 1) {
      target = 0;
    }

    const story = state.stories[target];
    setActiveStory({ ...story, isTrendsetter: story.isTrendsetter });
    setModalIsOpen(true);
  };

  const handleStoryCheerToggle = (id, cheerForUser, cheersCount) => {
    dispatch({
      type: "storyCheerToogle",
      storyId: id,
      cheerForUser,
      cheersCount
    });
  };

  const handleClose = () => {
    setModalIsOpen(false);
    history.push(`/stories/trend/${storyQuestionId}`);
  };

  const handleCommentsChange = (story, numberOfComments) => {
    dispatch({
      type: "storyCommentsUpdated",
      storyId: story.id,
      numberOfComments
    });
  };

  const handleCommentClick = () => {
    dispatch({ type: "toogleShowComments" });
  };

  // Get the new element of the userActivity (the new story)
  const getNewStoryFromUA = userActivity => (userActivity.childUserActivities.length === 0
    ? userActivity.entity
    : userActivity.childUserActivities[0].entity);

  const onNotificationClicked = activities => {
    const { stories } = state;
    if (stories.length >= 24) {
      stories.pop();
    }
    activities.forEach(activity => {
      dispatch({ type: "updateUserActivity", userActivity: activity });
      dispatch({
        type: "storyCreated",
        story: getNewStoryFromUA(activity),
        stories
      });
    });
  };

  const handleStoryPosted = story => {
    dispatch({ type: "childStoryCreated", story });
  };

  const handleStoryQuestionUpdate = storyQuestion => {
    dispatch({ type: "storyQuestionUpdated", storyQuestion });
  };

  if (state.error && state.error.status === 404) {
    return <NotFound />;
  }

  if (state.error) {
    return <ErrorMessage error={state.error} />;
  }

  if (isParentPrompt || isAIPrompt) {
    return (
      <div className="New-Story-Trend">
        {(!isLoggedIn || isCrossTenant) && <PublicToolbar />}
        {isCrossTenant && (<CrossTenantWarning orgName={state.organization?.name} />)}
        <div
          className={`Feed--column Stories col-xs-12 col-md-8 ${
            !instructionalVideoMedia && !isLoggedIn && !isAIPrompt ? "col-md-offset-2" : ""
          }`}
        >
          {state.fetching ? (
            <>
              <FeedItemPlaceholder />
              <FeedItemPlaceholder />
              <FeedItemPlaceholder />
            </>
          ) : (
            <ErrorBoundary>
              <StoryTrendNotification
                currentActivity={state.userActivity}
                questionId={storyQuestionId}
                onNotificationClicked={onNotificationClicked}
              />
              {activeStory && (
                <LightboxStories
                  modalIsOpen={modalIsOpen}
                  activeStory={activeStory}
                  currentUserAnswered={state.currentUserAnswered}
                  stories={state.stories}
                  onCommentsChange={handleCommentsChange}
                  onClose={handleClose}
                  onStoryChange={handleThumbnailClick}
                  onDelete={onStoryRemove}
                  onStoryCheerToggle={handleStoryCheerToggle}
                  onStoryUpdate={onStoryUpdate}
                  isTrendsetter={activeStory.isTrendsetter}
                />
              )}
              <div className="FeedItem__preview Stories__title-container">
                <div className="FeedItem__preview__photo_created Story">
                  <div className="Story-Header" style={{ backgroundImage: `url(${state.storyQuestion.imageUrl})` }}>
                    <div className={isAIPrompt ? "Story-Header__Overlay-AiOverlay" : "Story-Header__Overlay"}>
                      {isAIPrompt && (
                        <div className="ai-assisted-prompt">
                          <img src={magicWandIcon} alt="Magic Wand" />
                          <span>AI-Assisted Prompt</span>
                        </div>
                      )}
                      <StoryQuestionTitle storyQuestion={state.storyQuestion} />
                      {isAdmin(profile) && !isAIPrompt && !isCrossTenant && (
                        <>
                          <NewStoryQuestionModal
                            storyQuestion={state.storyQuestion}
                            onUpdate={handleStoryQuestionUpdate}
                            selectedOptions={
                              state.storyQuestion.storyQuestionTags
                            }
                          >
                            {onTrigger => (
                              <PlainButton
                                className="Edit-Btn"
                                onClick={onTrigger}
                              >
                                <Icon icon="edit-outlined" />
                              </PlainButton>
                            )}
                          </NewStoryQuestionModal>
                        </>
                      )}
                      <span id="instructions-context" className="hidden">Instructions to this prompt</span>
                      <p aria-labelledby="instructions-context" className="FeedItem__preview__title__Summary">
                        {state.storyQuestion.summary}
                      </p>
                      {state.storyQuestion.summary?.length > 270 && (
                        <StoryTrendSummary
                          prompt={state.storyQuestion.question}
                          summary={state.storyQuestion.summary}
                          title="Story Overview"
                        />
                      )}
                      {instructionalVideoMedia && (
                        <WatchIntroVideo
                          instructionalVideoMedia={instructionalVideoMedia}
                          style={{ bottom: "16px" }}
                        />
                      )}
                      {isLoggedIn && !isTablet && !isCrossTenant && (
                        <PromptShareOptions
                          organization={currentOrganization}
                          storyQuestion={state.storyQuestion}
                        />
                      )}
                      {!isMobile && (
                        <>
                          <QRCode
                            aria-label={t("QR code to this prompt")}
                            bgColor="transparent"
                            className="QR"
                            fgColor="#FFFFFF"
                            size={isAIPrompt ? 120 : 128}
                            value={`https://${getSubdomain()}.culturehq.com/${path}#create`}
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {isAIPrompt && isTablet && !isStoryQuestionPausedStatus && (
                <div className="col-xs-12 Stories__Button">
                  <PostStoryModal
                    preFilledQuestion={state.storyQuestion}
                    organization={currentOrganization}
                  />
                </div>
              )}
              {!isAIPrompt && (
                <AnswerPrompts
                  isParentTrendPausedStatus={isStoryQuestionPausedStatus}
                  currentOrganization={currentOrganization}
                  childStoryQuestions={state.storyQuestion.childStoryQuestions}
                  onPosted={handleStoryPosted}
                  parentId={state.storyQuestion.id}
                  profile={profile}
                  onDeleteStory={onStoryRemove}
                  onUpdateStory={onStoryUpdate}
                  onNotification={setNotification}
                  noOptions
                />
              )}
              <div className="col-xs-12 Stories-Container">
                <div className="Stories-Container__Header">
                  <h3 className="Stories-Container__Header--Title">
                    <Trans>STORIES</Trans>
                  </h3>
                  <VideoStudioContext
                    montage
                    onStoryUpdate={onStoryUpdate}
                    stories={state.stories}
                    storyQuestionId={state.storyQuestion.id}
                    trend={getTrend(state.storyQuestion)}
                    disabled={!showCreateMontageButton}
                  />
                </div>
                <UserActivityView
                  isStoryQuestionPausedStatus={isStoryQuestionPausedStatus}
                  state={state}
                  handleThumbnailClick={handleThumbnailClick}
                  handleCommentClick={handleCommentClick}
                  handleCommentsChange={handleCommentsChange}
                  isParentPrompt={isParentPrompt}
                  onNotification={setNotification}
                  currentOrganization={currentOrganization}
                />
              </div>
            </ErrorBoundary>
          )}
        </div>
        <div className="Story-Trend--right New-Story-Trend hidden-xs hidden-sm col-md-4">
          <Loader loading={state.fetching}>
            {instructionalVideoMedia && (
              <InstructionalVideo media={instructionalVideoMedia} />
            )}
            {isLoggedIn && !isAIPrompt && !isCrossTenant && (
              <>
                <DiscoverStories currentStoryId={state.storyQuestion?.id} />
                <StartTheTrend currentStoryId={state.storyQuestion?.id} />
              </>
            )}
            {isAIPrompt && (
              <>
                {!isStoryQuestionPausedStatus && (
                  <PostStoryModal
                    preFilledQuestion={state.storyQuestion}
                    organization={currentOrganization}
                  />
                )}
                {!isTablet && <StoryPromptaiAssistant />}
              </>
            )}
          </Loader>
        </div>
        {notification && (<PublicNotificationPopup notification={notification} />)}
      </div>
    );
  }

  return (
    <>
      {(!isLoggedIn || isCrossTenant) && <PublicToolbar />}
      {isCrossTenant && (<CrossTenantWarning orgName={state.organization?.name} />)}
      <div
        className={`Feed--column Stories col-xs-12 col-md-8 ${
          !isLoggedIn && !isAIPrompt ? "col-md-offset-2" : ""
        }`}
      >
        <div className="col-xs-12 visible-xs visible-sm Stories__Button">
          {state.fetching ? <Loader loading />
            : (
              <>
                <PostStory
                  organization={currentOrganization}
                  preFilledQuestion={state.storyQuestion}
                  openModal
                  onDeleteStory={onStoryRemove}
                  onUpdateStory={onStoryUpdate}
                  onNotification={setNotification}
                >
                  {onTrigger => (
                    <Button onClick={onTrigger}>
                      <Icon icon="book" />
                      <Trans>Add story</Trans>
                    </Button>
                  )}
                </PostStory>
                {isLoggedIn && !isStoryQuestionPausedStatus && !isCrossTenant && (
                  <ShareQuickLinks storyQuestion={state.storyQuestion} className="quick-inline" nextToMenu={!!state.userActivity} />
                )}
              </>
            )}
        </div>
        {state.fetching ? (
          <>
            <FeedItemPlaceholder />
            <FeedItemPlaceholder />
            <FeedItemPlaceholder />
          </>
        ) : (
          <ErrorBoundary>
            <StoryTrendNotification
              currentActivity={state.userActivity}
              questionId={storyQuestionId}
              onNotificationClicked={onNotificationClicked}
            />
            <div className="col-xs-12 Stories__container">
              {activeStory && (
                <LightboxStories
                  modalIsOpen={modalIsOpen}
                  activeStory={activeStory}
                  currentUserAnswered={state.currentUserAnswered}
                  stories={state.stories}
                  onCommentsChange={handleCommentsChange}
                  onClose={handleClose}
                  onStoryChange={handleThumbnailClick}
                  onDelete={onStoryRemove}
                  onStoryCheerToggle={handleStoryCheerToggle}
                  onStoryUpdate={onStoryUpdate}
                  isTrendsetter={activeStory.isTrendsetter}
                />
              )}
              <div className="FeedItem__preview Stories__title-container">
                <div className="FeedItem__preview__photo_created Story">
                  {isLoggedIn && !isStoryQuestionPausedStatus && !isCrossTenant && (
                    <ShareQuickLinks
                      storyQuestion={state.storyQuestion}
                      className="quick-inline"
                      nextToMenu={!!state.userActivity}
                    />
                  )}
                  {!isLoggedIn && !isStoryQuestionPausedStatus && (
                    <div className="DesktopAddStoryButton">
                      <Loader loading={state.fetching}>
                        <PostStory
                          organization={currentOrganization}
                          preFilledQuestion={state.storyQuestion}
                          openModal
                          initialOpen={location.hash === "#create"}
                          onDeleteStory={onStoryRemove}
                          onUpdateStory={onStoryUpdate}
                          onNotification={setNotification}
                        >
                          {onTrigger => (
                            <Button onClick={onTrigger}>
                              <Trans>Add your story!</Trans> +
                              {state.storyQuestion?.points ?? 50}
                            </Button>
                          )}
                        </PostStory>
                      </Loader>
                    </div>
                  )}
                  {isLoggedIn && (
                    <QuickLinks
                      story={state.userActivity.entity}
                      userActivity={state.userActivity}
                      isAtFeed={false}
                      onStoryUpdate={onStoryUpdate}
                      stories={state.stories}
                      storyQuestionId={state.storyQuestion.id}
                      trend={getTrend(state.storyQuestion)}
                      showCreateMontageButton={showCreateMontageButton}
                      onUpdate={handleStoryQuestionUpdate}
                    />
                  )}
                  {state.userActivity?.id && isLoggedIn && (
                    <FeedItemTitle item={state.userActivity} date={state.userActivity.bumpedAt}>
                      {state.userActivity.users?.length > 0 && (
                        <ProfileLink user={state.userActivity.users[0]} />
                      )}
                      {state.usersCount > 1 && (
                        <span>
                          <Trans>
                            {" "}
                            and {state.usersCount - 1} other
                            {state.usersCount > 2 ? "s" : ""}
                          </Trans>
                        </span>
                      )}
                      <Trans> added their story</Trans>
                    </FeedItemTitle>
                  )}
                  <StoryQuestionTitle
                    storyQuestion={state.storyQuestion}
                    showLink
                  />
                </div>
              </div>
              <UserActivityView
                isStoryQuestionPausedStatus={isStoryQuestionPausedStatus}
                state={state}
                handleThumbnailClick={handleThumbnailClick}
                handleCommentClick={handleCommentClick}
                handleCommentsChange={handleCommentsChange}
                isParentPrompt={isParentPrompt}
                onNotification={setNotification}
                profile={profile}
              />
            </div>
          </ErrorBoundary>
        )}
        {notification && (<PublicNotificationPopup notification={notification} />)}
      </div>
      {isLoggedIn && !isStoryQuestionPausedStatus && (
        <div className="Story-Trend--right hidden-xs hidden-sm col-md-4">
          <Loader loading={state.fetching}>
            <>
              <PostStory
                organization={currentOrganization}
                preFilledQuestion={state.storyQuestion}
                openModal
                initialOpen={location.hash === "#create"}
                onDeleteStory={onStoryRemove}
                onUpdateStory={onStoryUpdate}
                onNotification={setNotification}
              >
                {onTrigger => (
                  <Button onClick={onTrigger}>
                    <Trans>Add your story!</Trans> +
                    {state.storyQuestion?.points ?? 50}
                  </Button>
                )}
              </PostStory>
              {!isMobile && (
                <div className="Story-Trend--qr">
                  <div className="Story-Trend--label-container">
                    <span className="Story-Trend--text"><Trans>Answer this story on mobile</Trans></span>
                    <Icon className="Subprompt-Arrow" icon="nested-element" />
                  </div>
                  <div className="Story-Trend--qr-code">
                    <QRCode
                      aria-label={t("QR code to this prompt")}
                      bgColor="transparent"
                      className="QR"
                      fgColor="#5c5f67"
                      size={isAIPrompt ? 120 : 128}
                      value={`https://${getSubdomain()}.culturehq.com/stories/trend/${state.storyQuestion?.id}#create`}
                    />
                  </div>
                </div>
              )}
              {!isCrossTenant && (
                <>
                  <DiscoverStories currentStoryId={state.storyQuestion?.id} />
                  <StartTheTrend currentStoryId={state.storyQuestion?.id} />
                </>
              )}
            </>
          </Loader>
        </div>
      )}
    </>
  );
};

const StoryTrendCallout = ({ match }) => {
  const feedRef = useRef();

  return (
    <div className="Story-Trend Container-left_nav">
      <div className="clearfix col-xs-12">
        <div className="row" ref={feedRef}>
          <StoryItems
            feedRef={feedRef}
            storyQuestionId={match.params.storyQuestionId}
            storyId={match.params.storyId}
            promptId={match.params.promptId}
          />
        </div>
      </div>
    </div>
  );
};

export default StoryTrendCallout;
